import { extractUiChunkEnvelope } from "../../../../utils/ChatEventReducer";
import { critical as _crit } from "../../../../utils/ChatStreamLogger";
import { validateUiChunkEnvelope } from "./chunkSchema";
import { applyUiChunk, createUiChunkSessionState } from "./processUiChunkStream";
import { mergeStreamTurnWithStore } from "./mergeStreamTurnParts";

/**
 * Live stream controller for the agent chat.
 *
 * Owns the throttled write path for SSE `ui_chunk` events: the per-session
 * reducer cursors, the intra-frame flush buffer, and the animation-frame
 * throttle. It writes the in-flight assistant turn straight into
 * `sessionDetail.messages` (the single source of truth) via the store's
 * `_upsertMessage`, so there is never a second copy of the streamed messages.
 *
 * It is deliberately a thin orchestration layer over the pure
 * {@link applyUiChunk} reducer: the reducer decides message shape, this
 * controller decides *when* those shapes are committed to the store.
 *
 * The controller is created once per store and injected with store accessors
 * and the handful of store-owned helpers it must call back into. It keeps no
 * React/Zustand knowledge beyond `getState`/`setState`.
 *
 * @param {object} deps
 * @param {() => object} deps.getState Store getState accessor.
 * @param {(patch: object) => void} deps.setState Store setState accessor.
 * @param {(status: string) => boolean} deps.isTerminalForStreaming
 *   True when a session status means streaming is over (final messages are
 *   authoritative and chunks must not be replayed into them).
 * @param {(reason: string) => void} deps.markChatStatusReason Records why the
 *   next chatStatus transition happened (diagnostic breadcrumb).
 * @param {(msg: object) => boolean} deps.isEmptyAssistantStreamMessage True for
 *   the transient parts-less assistant turn that `start` creates, which must
 *   not be committed to the store.
 */
export function createStreamController({
  getState,
  setState,
  isTerminalForStreaming,
  markChatStatusReason,
  isEmptyAssistantStreamMessage,
}) {
  let rafId = null;
  // Intra-frame buffer for the throttled live-stream flush: the running reduced
  // result for the current animation frame, or null when nothing is pending. A
  // single object so sessionId / messages / turnId can never drift out of sync.
  let pendingFlush = null; // { sessionId, messages, turnId } | null

  // Per-session stream cursor (pointer bookkeeping for the live UI-chunk
  // reducer). The reducer writes the assistant turn straight into
  // sessionDetail.messages, so the cursor holds no message copy —
  // sessionDetail.messages is the single source of truth.
  const cursorBySessionId = new Map();

  function sessionKey(sessionId) {
    return String(sessionId || "").trim();
  }

  function getCursor(sessionId) {
    const key = sessionKey(sessionId);
    if (!key) return createUiChunkSessionState("");
    const existing = cursorBySessionId.get(key);
    if (existing) return existing;
    const fresh = createUiChunkSessionState(key);
    cursorBySessionId.set(key, fresh);
    return fresh;
  }

  function setCursor(sessionId, cursor) {
    const key = sessionKey(sessionId);
    if (key) cursorBySessionId.set(key, cursor);
  }

  function clearCursor(sessionId) {
    const key = sessionKey(sessionId);
    if (!key) return;
    cursorBySessionId.delete(key);
  }

  // Only one session is subscribed to SSE at a time, so any other cursor is
  // stale the moment we (re)subscribe. Clearing the whole map on subscribe
  // reclaims cursors for sessions the user navigated away from mid-stream (no
  // finish/complete event ever arrived to clear them individually). We also
  // drain any in-flight throttled flush: a leftover pendingFlush for the same
  // session would otherwise seed the reducer base on the next chunk, defeating
  // the "fresh cursor + canonical store messages" rebuild (re)subscribe relies
  // on.
  function clearAllCursors() {
    cursorBySessionId.clear();
    takePendingFlush();
  }

  // Cancels the throttled flush frame and clears the intra-frame buffer,
  // returning what was pending (or null) so callers can flush it first.
  function takePendingFlush() {
    if (rafId) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }
    const pending = pendingFlush;
    pendingFlush = null;
    return pending;
  }

  // Writes the streamed assistant turn (resolved by id out of the reducer's
  // working array) into sessionDetail.messages via the shared upsert path, so a
  // concurrent message_added for another message is never clobbered.
  function upsertStreamTurn(sessionId, messages, turnId) {
    if (!turnId || !Array.isArray(messages)) return;
    let turn = messages.find((m) => m.id === turnId);
    if (!turn || isEmptyAssistantStreamMessage(turn)) return;

    const detail = getState().sessionDetail;
    if (detail?.chatSessionId === sessionId) {
      const existing = (detail.messages || []).find(
        (m) => (m.messageId && m.messageId === turnId) || (m.id && m.id === turnId)
      );
      if (existing) {
        turn = mergeStreamTurnWithStore(existing, turn);
      }
    }

    getState()._upsertMessage(sessionId, {
      ...turn,
      role: "assistant",
      messageId: turn.id,
    });
  }

  function invalidatePendingFlushForSession(sessionId, messageId) {
    if (pendingFlush && pendingFlush.sessionId === sessionId &&
        (!messageId || pendingFlush.turnId === messageId)) {
      takePendingFlush();
    }
  }

  // Writes a buffered flush (the object returned by takePendingFlush) into the
  // store. No-op when nothing was pending. Single drain path shared by the
  // throttled animation frame and the explicit session_complete flush.
  function flushStreamTurn(pending) {
    if (pending && pending.sessionId && pending.messages) {
      upsertStreamTurn(pending.sessionId, pending.messages, pending.turnId);
    }
  }

  // The reducer is the single source of truth for both live and persisted
  // turns. It reads the current assistant turn out of sessionDetail.messages
  // (via the per-session cursor's pointers), appends/updates it, and we write
  // the turn back by id. No separate builder-store copy of the messages exists.
  function applyStreamChunk(eventName, data, sessionId, seq = null) {
    const envelope = extractUiChunkEnvelope(eventName, data);
    const validation = validateUiChunkEnvelope(envelope);
    if (!validation.ok) {
      _crit(
        "AgentStore",
        "warn",
        `dropping invalid ui_chunk envelope: ${validation.error || "invalid ui_chunk envelope"}`
      );
      return;
    }
    const chunk = validation.value.chunk;

    // Don't replay chunks into an already-finalized turn: the snapshot holds the
    // authoritative final messages and replaying would duplicate them.
    if (isTerminalForStreaming(getState().sessionDetail?.status)) {
      return;
    }

    const detail = getState().sessionDetail;
    if (!detail || detail.chatSessionId !== sessionId) {
      // Chunk for a session we are not currently viewing — drop it.
      return;
    }

    // Intra-frame accumulation: chunks can arrive faster than animation frames,
    // and the flush to the store is throttled to one per frame. Build on the
    // pending working array (this frame's running result) so deltas within a
    // frame are not lost; otherwise seed from the canonical store array.
    const prevCursor = getCursor(sessionId);
    const prevTurnId = prevCursor.currentMessageId;
    // A frame can span multiple assistant turns. Commit the previous turn
    // before replacing the single-turn buffer, including in background tabs.
    if (chunk.type === "start" && chunk.messageId &&
        prevTurnId && chunk.messageId !== prevTurnId) {
      flushPendingForSession(sessionId);
    }
    const base =
      pendingFlush && pendingFlush.sessionId === sessionId
        ? pendingFlush.messages
        : (getState().sessionDetail?.messages || []).filter((message) =>
            prevCursor.streamedMessageIds.includes(message.id) || message.id === chunk.messageId);

    const prevStreamedIds = Array.isArray(prevCursor.streamedMessageIds)
      ? prevCursor.streamedMessageIds
      : [];
    const { cursor, messages } = applyUiChunk(prevCursor, chunk, base);
    const updatedId = cursor.currentMessageId || prevTurnId;
    const updatedIndex = messages.findIndex((message) => message.id === updatedId);
    if (updatedIndex >= 0) messages[updatedIndex] = { ...messages[updatedIndex],
      _streamSource: "live", _streamSequence: seq };
    setCursor(sessionId, cursor);
    if (cursor.lastError) {
      _crit(
        "AgentStore",
        "warn",
        `ui_chunk reducer error [${cursor.lastError.code}]: ${cursor.lastError.message}`
      );
    }

    // `finish` clears currentMessageId, so fall back to the pre-finish turn id.
    const turnId = cursor.currentMessageId || prevTurnId;

    if (chunk.type === "finish") {
      takePendingFlush();
      // Drain even during cancellation: session_complete marks this persisted
      // partial turn interrupted, and must not lose its last buffered tokens.
      // A single finish can finalize multiple turns of a multi-iteration run,
      // so flush every turn this stream opened (not just the current one).
      const idsToFlush = prevStreamedIds.length
        ? prevStreamedIds
        : turnId
          ? [turnId]
          : [];
      for (const id of idsToFlush) {
        upsertStreamTurn(sessionId, messages, id);
      }
      return;
    }

    if (getState().chatStatus === "submitted") {
      // submitted→streaming on the first chunk is owned by the sequence-gated
      // lifecycle reducer. Route a ui_chunk activity input (no backend status)
      // carrying the chunk's seq so the advance is gated like every other event.
      getState()._applyLifecycle({
        kind: "sse",
        sessionId,
        eventType: "ui_chunk",
        seq,
        backendStatus: null,
      });
    }

    // Throttle live writes to one per animation frame. The transient empty
    // assistant turn created by `start` is coalesced away here, and the render
    // layer ignores parts-less assistant turns regardless.
    pendingFlush = { sessionId, messages, turnId };
    if (!rafId) {
      rafId = requestAnimationFrame(() => {
        rafId = null;
        flushStreamTurn(takePendingFlush());
      });
    }
  }

  // Flushes any not-yet-flushed live turn for the given session (e.g. a cancel
  // that lands between the last chunk and its scheduled animation frame), so the
  // final assistant turn is in sessionDetail.messages before finalizing.
  function flushPendingForSession(sessionId) {
    if (pendingFlush?.sessionId === sessionId) {
      flushStreamTurn(takePendingFlush());
    }
  }

  return {
    applyStreamChunk,
    finishSession: (sessionId) => {
      flushPendingForSession(sessionId);
      const cursor = getCursor(sessionId);
      const ids = cursor.streamedMessageIds.length ? cursor.streamedMessageIds : cursor.finishedMessageIds || [];
      const detail = getState().sessionDetail;
      if (detail?.chatSessionId !== sessionId) return [];
      const finalized = applyUiChunk(cursor, { type: "finish" }, detail.messages || []);
      for (const id of ids) upsertStreamTurn(sessionId, finalized.messages, id);
      setCursor(sessionId, finalized.cursor);
      return ids;
    },
    flushPendingForSession,
    invalidatePendingFlushForSession,
    clearCursor,
    clearAllCursors,
    restoreCursor: (sessionId, cursor) => {
      invalidatePendingFlushForSession(sessionId);
      setCursor(sessionId, cursor);
    },
  };
}
